import { useTranslation } from "react-i18next";
import { cn, formatMessageTimestamp } from "@/lib/utils";
import { MessageSendingStatus } from "@/indexedDB/types";
import {
  Check,
  CheckCheck,
  CircleDashed,
  Pin,
  LoaderCircle,
} from "lucide-react";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  timestamp: string;
  isOutgoing: boolean;
  isPinned?: boolean;
  sendingStatus: MessageSendingStatus;
  /**
   * The row is FAILED but its delivery could not be confirmed (#1177): its send
   * may have reached the server. Rendered as a neutral "Not confirmed sent"
   * marker instead of the red failed tick.
   */
  deliveryUnconfirmed?: boolean;
}

export default function MessageTimestamp({
  timestamp,
  isOutgoing,
  isPinned = false,
  sendingStatus,
  deliveryUnconfirmed = false,
  className,
}: Props) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-1 align-middle text-[0.7rem]",
        className,
      )}
    >
      <div
        className={cn(
          "flex gap-1 rounded-md px-1",
          isOutgoing
            ? "bg-primary/20 dark:bg-primary dark:text-primary-foreground"
            : "bg-accent text-accent-foreground",
        )}
      >
        {isPinned && <Pin className="size-4 opacity-70" />}
        <span className="opacity-70">{formatMessageTimestamp(timestamp)}</span>
        {isOutgoing && (
          <>
            {sendingStatus === MessageSendingStatus.PENDING && (
              <LoaderCircle
                strokeWidth={1}
                className="size-4 shrink-0 animate-spin opacity-50"
              />
            )}
            {sendingStatus === MessageSendingStatus.SENT && (
              <Check strokeWidth={1} className="size-4 opacity-50" />
            )}
            {sendingStatus === MessageSendingStatus.DELIVERED && (
              <CheckCheck strokeWidth={1} className="size-4 opacity-50" />
            )}
            {sendingStatus === MessageSendingStatus.READ && (
              <CheckCheck strokeWidth={1} className="size-4" />
            )}
            {sendingStatus === MessageSendingStatus.FAILED &&
              (deliveryUnconfirmed ? (
                // Terminal state that never self-resolves → show visible text, not
                // a lone glyph (a dashed circle at 0.7rem is indistinguishable from
                // "sending"/"sent"). Icon is decorative; the text is the label.
                <span className="flex items-center gap-0.5 opacity-70">
                  <CircleDashed
                    strokeWidth={1}
                    className="size-3.5 shrink-0"
                    aria-hidden="true"
                  />
                  <span>{t("notConfirmedSent")}</span>
                </span>
              ) : (
                <CheckCheck
                  strokeWidth={1}
                  className="size-4 text-destructive"
                />
              ))}
          </>
        )}
      </div>
    </div>
  );
}
